import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import StructuredContent from '@nerdwallet/structured-content';
import { Text } from '@nerdwallet/currency';

import CollapsibleParagraph from '../../../collapsible-paragraph/collapsible-paragraph';
import Paragraph from '../../../paragraph/paragraph';
import { numberInputSpec } from '../../../../lib/editing-configs';

import {
  generateAbstract,
  isEligibleToExpand,
  isStructuredContent,
} from './generate-abstract';

/**
 * Collapsible Paragraph that accomodates both string and structured content input.
 */
const StructuredCollapsibleParagraph = ({ numberOfSentences, content }) => {
  const shouldRenderStructuredContent = isStructuredContent(content);
  const renderContent = useCallback(
    (inputContent) =>
      shouldRenderStructuredContent && inputContent ? (
        <StructuredContent>{inputContent}</StructuredContent>
      ) : (
        inputContent
      ),
    [shouldRenderStructuredContent]
  );

  const abstract = useMemo(
    () => renderContent(generateAbstract(content, numberOfSentences)),
    [renderContent, content, numberOfSentences]
  );
  const expandedContent = useMemo(
    () => (
      <Paragraph
        dangerouslyAllowHTMLIn={{ body: !shouldRenderStructuredContent }}
        smallText
        body={renderContent(content)}
      />
    ),
    [renderContent, content, shouldRenderStructuredContent]
  );

  if (!content) {
    return null;
  }

  /**
   * If the content isn't long enough don't bother rendering it as expandable.
   */
  if (!isEligibleToExpand(content, numberOfSentences)) {
    return expandedContent;
  }

  return (
    <CollapsibleParagraph
      dangerouslyAllowHTMLIn={{ body: !shouldRenderStructuredContent }}
      abstractText={abstract}
      reuseAbstract={false}
      smallText
    >
      {expandedContent}
    </CollapsibleParagraph>
  );
};

StructuredCollapsibleParagraph.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  numberOfSentences: PropTypes.number,
};

const TableCollapsibleParagraph = ({
  numSentences,
  title,
  paragraphPath,
  marketplaceEntity,
}) => {
  const paragraph = _.get(marketplaceEntity, paragraphPath);
  if (!paragraph) {
    return null;
  }

  return (
    <div style={{ minWidth: '218px' }} data-integration="collapsible-paragraph">
      <Text size="small" bold>
        {title}
      </Text>
      <StructuredCollapsibleParagraph
        numberOfSentences={numSentences}
        content={paragraph}
      />
    </div>
  );
};

TableCollapsibleParagraph.propTypes = {
  numSentences: PropTypes.number,
  title: PropTypes.string,
  paragraphPath: PropTypes.string,
  marketplaceEntity: PropTypes.object,
};

TableCollapsibleParagraph.defaultProps = {
  numSentences: 6,
  paragraphPath: '',
  marketplaceEntity: {},
};

TableCollapsibleParagraph.getEditingConfig = () => [
  numberInputSpec(
    'numSentences',
    'Number of sentences visible before the "show more"'
  ),
];

export default TableCollapsibleParagraph;
